import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Compass, MessageCircle, PlusSquare, User, Wand2, Search, LogOut } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import LanguagePopup from './LanguagePopup';

interface MobileSidebarProps {
  isOpen: boolean;
  onClose?: () => void;
}

const MobileSidebar: React.FC<MobileSidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { isAuthenticated, logout } = useAuth();
  const [isLanguagePopupOpen, setIsLanguagePopupOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  
  const isActive = (path: string): boolean => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    if (isLoggingOut) return;
    
    try {
      setIsLoggingOut(true);
      await logout();
      toast.success('Successfully logged out');
      
      // Close the sidebar after logout
      if (onClose) {
        onClose();
      }
      
      // Navigate to home page
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
      toast.error('Failed to log out. Please try again.');
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <>
      <div className={`fixed inset-0 z-50 ${isOpen ? 'block' : 'hidden'}`}>
        <div 
          className="fixed inset-0 bg-black/30" 
          onClick={onClose}
          aria-hidden="true"
        />
        
        <div className="fixed inset-y-0 left-0 w-64 bg-white shadow-xl flex flex-col h-full">
          {/* Logo */}
          <div className="p-5 border-b">
            <Link to="/" className="text-xl font-bold">
              WANTTD
            </Link>
          </div>
          
          {/* Search box */}
          <div className="p-5">
            {/* <SidebarSearch /> */}
          </div>
          
          {/* Navigation links */}
          <nav className="flex-1 px-5 space-y-1">
            <Link 
              to="/" 
              className={`flex items-center space-x-2 p-2 rounded-md text-sm ${isActive('/') ? 'font-medium' : 'text-gray-700'}`}
              onClick={onClose}
            >
              <Home className="h-5 w-5" />
              <span>Home</span>
            </Link>
            
            {/* <Link 
              to="/explore" 
              className={`flex items-center space-x-2 p-2 rounded-md text-sm ${isActive('/explore') ? 'font-medium' : 'text-gray-700'}`}
              onClick={onClose}
            >
              <Compass className="h-5 w-5" />
              <span>Explore</span>
            </Link> */}
            
            <Link 
              to="/search" 
              className={`flex items-center space-x-2 p-2 rounded-md text-sm ${isActive('/search') ? 'font-medium' : 'text-gray-700'}`}
              onClick={onClose}
            >
              <Search className="h-5 w-5" />
              <span>Search</span>
            </Link>
            
            {/* Messages - requires authentication */}
            {isAuthenticated ? (
              <Link 
                to="/chat" 
                className={`flex items-center space-x-2 p-2 rounded-md text-sm ${isActive('/chat') ? 'font-medium' : 'text-gray-700'}`}
                onClick={onClose}
              >
                <MessageCircle className="h-5 w-5" />
                <span>Messages</span>
              </Link>
            ) : (
              <Link 
                to="/signin" 
                className="flex items-center space-x-2 p-2 rounded-md text-sm text-gray-700"
                onClick={onClose}
                state={{ from: '/chat', message: 'Sign in to access your messages' }}
              >
                <MessageCircle className="h-5 w-5" />
                <span>Messages</span>
              </Link>
            )}
            
            {/* Create Want - requires authentication */}
            {isAuthenticated ? (
              <Link 
                to="/create-will" 
                className={`flex items-center space-x-2 p-2 rounded-md text-sm ${isActive('/create-will') ? 'font-medium' : 'text-gray-700'}`}
                onClick={onClose}
              >
                <PlusSquare className="h-5 w-5" />
                <span>Want</span>
              </Link>
            ) : (
              <Link 
                to="/signin" 
                className="flex items-center space-x-2 p-2 rounded-md text-sm text-gray-700"
                onClick={onClose}
                state={{ from: '/create-will', message: 'Sign in to create wants' }}
              >
                <PlusSquare className="h-5 w-5" />
                <span>Want</span>
              </Link>
            )}
            
            {/* My Genies - requires authentication */}
            {isAuthenticated ? (
              <Link 
                to="/my-genies" 
                className={`flex items-center space-x-2 p-2 rounded-md text-sm ${isActive('/my-genies') ? 'font-medium' : 'text-gray-700'}`}
                onClick={onClose}
              >
                <Wand2 className="h-5 w-5" />
                <span>My Genies</span>
              </Link>
            ) : (
              <Link 
                to="/signin" 
                className="flex items-center space-x-2 p-2 rounded-md text-sm text-gray-700"
                onClick={onClose}
                state={{ from: '/my-genies', message: 'Sign in to access your genies' }}
              >
                <Wand2 className="h-5 w-5" />
                <span>My Genies</span>
              </Link>
            )}
            
            {/* Profile - dynamic link based on auth state */}
            {isAuthenticated ? (
              <Link 
                to="/profile" 
                className={`flex items-center space-x-2 p-2 rounded-md text-sm ${isActive('/profile') ? 'font-medium' : 'text-gray-700'}`}
                onClick={onClose}
              >
                <User className="h-5 w-5" />
                <span>Profile</span>
              </Link>
            ) : (
              <Link 
                to="/signin" 
                className="flex items-center space-x-2 p-2 rounded-md text-sm text-gray-700"
                onClick={onClose}
                state={{ from: '/profile', message: 'Sign in to access your profile' }}
              >
                <User className="h-5 w-5" />
                <span>Profile</span>
              </Link>
            )}
          </nav>
          
          {/* Login/Logout button */}
          <div className="p-5">
            {isAuthenticated ? (
              // Logout button for authenticated users
              <button 
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="w-full py-2 flex justify-center items-center bg-gray-100 hover:bg-gray-200 rounded-md text-sm font-medium text-gray-800"
              >
                {isLoggingOut ? (
                  <>
                    <span className="mr-2">Logging out...</span>
                  </>
                ) : (
                  <>
                    <LogOut className="h-4 w-4 mr-2" />
                    <span>Log out</span>
                  </>
                )}
              </button>
            ) : (
              // Login link for non-authenticated users
              <Link to="/signin" onClick={onClose}>
                <button className="w-full py-2 bg-yellow-400 hover:bg-yellow-500 text-white rounded-md text-sm font-medium">
                  Log in
                </button>
              </Link>
            )}
          </div>
          
          {/* Footer links */}
          <div className="p-5 text-xs text-gray-500 space-x-2 border-t">
            <a href="https://about.wanttd.com/" target="_blank" rel="noopener noreferrer" className="hover:underline" onClick={onClose}>
              About
            </a>
            <span>·</span>
            <Link to="/privacy" onClick={onClose} className="hover:underline">
              Privacy
            </Link>
            <span>·</span>
            <Link to="/terms" onClick={onClose} className="hover:underline">
              Terms
            </Link>
            <span>·</span>
            <button 
              onClick={() => {
                setIsLanguagePopupOpen(true);
                // Don't close sidebar here as we want to show the language popup
              }}
              className="hover:underline"
            >
              Language
            </button>
          </div>
          
          {/* Copyright */}
          <div className="px-5 pb-5 text-xs text-gray-400">
            © 2024 WANTTD
          </div>
        </div>
      </div>
      
      {/* Language popup */}
      <LanguagePopup 
        isOpen={isLanguagePopupOpen} 
        onClose={() => setIsLanguagePopupOpen(false)} 
      />
    </>
  );
};

export default MobileSidebar; 